import * as z from "zod";
import { discriminatedUnion, literal, object } from "zod";
import { ContentType } from "../conversation";
import { CONCIERGE_CONVERSATION_CONSTANTS as C } from "./concierge-conversation-message-definition";
const CartActionableContentSchema = object({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.CART_ITEMS).describe(
    "This represents what entity the user can perform an action on.",
  ),
  [C.CONTENT_KEY]: object({
    [C.PRODUCT_ID_KEY]: z.coerce
      .number()
      .describe("The product that will be created in the cart."),
    [C.VARIANT_ID_KEY]: z.coerce
      .number()
      .describe("The variant of the product that will be created in the cart."),
    [C.QUANTITY_KEY]: z.coerce
      .number()
      .describe("The quantity of the product to create in the cart."),
  }).describe(
    "This represents the specific parameters that will be passed when the user performs the action.",
  ),
});

const ProductsDisplayActionableContentSchema = object({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.PRODUCT_DISPLAY).describe(
    "This represents the type of actionable content being displayed.",
  ),
  [C.CONTENT_KEY]: object({
    productsToDisplay: z.array(
      object({
        [C.PRODUCT_ID_KEY]: z.coerce
          .number()
          .describe("The productId of the product to display."),
      }).describe("The products to display to the customer."),
    ),
  }),
});

const DisplayOrderTrackingInfoActionableContentSchema = object({
  [C.CONTENT_TYPE_KEY]: literal(
    ContentType.DISPLAY_ORDER_TRACKING_INFO,
  ).describe("This represents the type of actionable content being displayed."),
  [C.CONTENT_KEY]: object({
    orderId: z
      .string()
      .describe("The order id of the order tracking info to display."),
    email: z
      .string()
      .describe("The email of the order tracking info to display."),
  }),
});

export const EmailInputActionableContentSchema = object({
  [C.CONTENT_TYPE_KEY]: literal(ContentType.EMAIL_INPUT).describe(
    "This represents the type of actionable content being displayed.",
  ),
  [C.CONTENT_KEY]: object({
    customerEmail: z.union([z.string(), z.null()]),
    customerInputContent: literal(true),
  }),
});

const ProductVisualizationActionableContentSchema = object({
  [C.CONTENT_TYPE_KEY]: literal(
    ContentType.PRODUCT_VISUALIZATION_UPLOAD_UI,
  ).describe("This represents the type of actionable content being displayed."),
  [C.CONTENT_KEY]: object({
    productId: z.string().describe("The product id for visualization."),
    variantId: z.string().optional().describe("The variant id if specified."),
    productImageUrl: z
      .string()
      .optional()
      .describe("The product image URL for display."),
  }),
});

export const ActionableContentSchema = discriminatedUnion(C.CONTENT_TYPE_KEY, [
  CartActionableContentSchema,
  ProductsDisplayActionableContentSchema,
  DisplayOrderTrackingInfoActionableContentSchema,
  EmailInputActionableContentSchema,
  ProductVisualizationActionableContentSchema,
]);

export type ActionableContent = z.infer<typeof ActionableContentSchema>;
